import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Modal } from "../components/ui/Modal";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { settingsService } from "../services/settingsService";
import { uploadImage } from "../services/uploadService";
import {
  Image as ImageIcon,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  Save,
  GripVertical,
  RefreshCw,
  BarChart3,
  Calendar,
  CheckCircle2,
  XCircle,
  Upload,
  Loader2,
} from "lucide-react";

interface Slide {
  _id: string;
  title: string;
  subtitle?: string;
  description?: string;
  image: string;
  mobileImage?: string;
  cta?: {
    text?: string;
    link?: string;
    openInNewTab?: boolean;
    backgroundColor?: string;
    textColor?: string;
  };
  overlay?: {
    enabled?: boolean;
    color?: string;
    opacity?: number;
  };
  textPosition?: "left" | "center" | "right";
  order: number;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  clickCount?: number;
  viewCount?: number;
}

interface SliderSettings {
  isActive: boolean;
  autoPlay: boolean;
  autoPlayInterval: number;
  showNavigation: boolean;
  showPagination: boolean;
  transitionEffect: "slide" | "fade" | "zoom";
  maxSlides: number;
}

type ModalMode = "add" | "edit" | "settings" | null;

export default function SliderManagementPage() {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [sliderSettings, setSliderSettings] = useState<SliderSettings>({
    isActive: true,
    autoPlay: true,
    autoPlayInterval: 5000,
    showNavigation: true,
    showPagination: true,
    transitionEffect: "slide",
    maxSlides: 7,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedSlide, setSelectedSlide] = useState<Slide | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<Slide>>({
    title: "",
    subtitle: "",
    description: "",
    image: "",
    mobileImage: "",
    cta: {
      text: "",
      link: "",
      openInNewTab: false,
      backgroundColor: "#007bff",
      textColor: "#ffffff",
    },
    overlay: {
      enabled: true,
      color: "rgba(0, 0, 0, 0.4)",
      opacity: 0.4,
    },
    textPosition: "center",
    isActive: true,
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setIsLoading(true);
    try {
      const response: any = await settingsService.getAllSlides();
      setSlides(response.slides || []);
      if (response.settings) {
        setSliderSettings({
          isActive: response.settings.isActive ?? true,
          autoPlay: response.settings.autoPlay ?? true,
          autoPlayInterval: response.settings.autoPlayInterval ?? 5000,
          showNavigation: response.settings.showNavigation ?? true,
          showPagination: response.settings.showPagination ?? true,
          transitionEffect: response.settings.transitionEffect ?? "slide",
          maxSlides: response.settings.maxSlides ?? 7,
        });
      }
    } catch (error) {
      console.error("Failed to fetch slides:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      title: "",
      subtitle: "",
      description: "",
      image: "",
      mobileImage: "",
      cta: {
        text: "Learn More",
        link: "/courses",
        openInNewTab: false,
        backgroundColor: "#007bff",
        textColor: "#ffffff",
      },
      overlay: {
        enabled: true,
        color: "rgba(0, 0, 0, 0.4)",
        opacity: 0.4,
      },
      textPosition: "center",
      isActive: true,
    });
    setModalMode("add");
  };

  const openEditModal = (slide: Slide) => {
    setSelectedSlide(slide);
    setFormData({
      title: slide.title,
      subtitle: slide.subtitle || "",
      description: slide.description || "",
      image: slide.image,
      mobileImage: slide.mobileImage || "",
      cta: {
        text: slide.cta?.text || "",
        link: slide.cta?.link || "",
        openInNewTab: slide.cta?.openInNewTab || false,
        backgroundColor: slide.cta?.backgroundColor || "#007bff",
        textColor: slide.cta?.textColor || "#ffffff",
      },
      overlay: {
        enabled: slide.overlay?.enabled ?? true,
        color: slide.overlay?.color || "rgba(0, 0, 0, 0.4)",
        opacity: slide.overlay?.opacity ?? 0.4,
      },
      textPosition: slide.textPosition || "center",
      isActive: slide.isActive,
      startDate: slide.startDate,
      endDate: slide.endDate,
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedSlide(null);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.image) {
      alert("Title and Image are required");
      return;
    }

    setIsSaving(true);
    try {
      if (modalMode === "add") {
        await settingsService.addSlide(formData);
      } else if (modalMode === "edit" && selectedSlide) {
        await settingsService.updateSlide(selectedSlide._id, formData);
      }
      await fetchSlides();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save slide:", error);
      alert(error.message || "Failed to save slide");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (slideId: string) => {
    if (deleteConfirm !== slideId) {
      setDeleteConfirm(slideId);
      return;
    }

    try {
      await settingsService.deleteSlide(slideId);
      await fetchSlides();
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete slide:", error);
      alert("Failed to delete slide");
    }
  };

  const handleToggleStatus = async (slideId: string) => {
    try {
      await settingsService.toggleSlideStatus(slideId);
      await fetchSlides();
    } catch (error) {
      console.error("Failed to toggle slide status:", error);
    }
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "image" | "mobileImage"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Use uploadService to upload to Cloudinary
      const url = await uploadImage(file, "sliders");
      setFormData((prev) => ({ ...prev, [field]: url }));
    } catch (error) {
      console.error("Failed to upload image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      // Reset input value to allow selecting same file again
      e.target.value = "";
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await settingsService.updateSliderSettings(sliderSettings);
      setModalMode(null);
      await fetchSlides();
    } catch (error) {
      console.error("Failed to save settings:", error);
      alert("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 max-w-7xl mx-auto">
        {/* Header and Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#102040]">
              Slider Management
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage homepage slider slides and settings
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setModalMode("settings")}
              className="h-11 px-4 border-[#E0E5F2] text-[#102040] hover:bg-gray-50 w-full sm:w-auto"
            >
              <SettingsIcon size={18} />
              Slider Settings
            </Button>
            <Button
              variant="primary"
              onClick={openAddModal}
              className="h-11 px-6 font-semibold shadow-lg shadow-amber-500/20 w-full sm:w-auto"
            >
              <Plus size={20} />
              Add new slide
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-5 border-none shadow-sm bg-white hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Total Slides
                </p>
                <p className="text-3xl font-bold text-[#102040]">
                  {slides.length}
                </p>
              </div>
              <div className="p-3.5 bg-blue-50 rounded-2xl">
                <ImageIcon className="h-7 w-7 text-blue-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-gray-400 font-medium">
              <span className="text-blue-500 mr-1.5">•</span>
              Max {sliderSettings.maxSlides} slides allowed
            </div>
          </Card>
          <Card className="p-5 border-none shadow-sm bg-white hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Active Slides
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {slides.filter((s) => s.isActive).length}
                </p>
              </div>
              <div className="p-3.5 bg-green-50 rounded-2xl">
                <Eye className="h-7 w-7 text-green-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-gray-400 font-medium">
              <span className="text-green-500 mr-1.5">•</span>
              Currently visible on homepage
            </div>
          </Card>
        </div>

        {/* Slides List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#102040]">Slide Items</h2>
            <span className="text-sm text-gray-500 font-medium">
              {slides.length} slides total
            </span>
          </div>

          {slides.length === 0 ? (
            <Card className="p-12 text-center border-2 border-dashed border-gray-100 bg-gray-50/50">
              <div className="p-4 bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <ImageIcon className="h-10 w-10 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-[#102040] mb-2">
                No slides created
              </h3>
              <p className="text-gray-500 max-w-xs mx-auto mb-6">
                Your homepage slider is currently empty. Add slides to make it
                visual and engaging.
              </p>
              <Button variant="primary" onClick={openAddModal}>
                <Plus size={18} />
                Add your first slide
              </Button>
            </Card>
          ) : (
            slides.map((slide) => (
              <Card
                key={slide._id}
                className="p-4 border-none shadow-sm hover:shadow-md transition-all duration-200 group relative bg-white overflow-hidden"
              >
                {/* Visual indicator for active status */}
                <div
                  className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                    slide.isActive ? "bg-[#e07828]" : "bg-gray-200"
                  }`}
                />

                <div className="flex flex-col sm:flex-row gap-5">
                  {/* Thumbnail and Drag Handle Column */}
                  <div className="flex gap-4 items-center">
                    <div className="text-gray-300 group-hover:text-gray-400 cursor-move transition-colors">
                      <GripVertical size={22} />
                    </div>

                    <div className="relative w-40 h-24 bg-gray-100 rounded-xl overflow-hidden shadow-inner flex-shrink-0 group-hover:ring-4 group-hover:ring-amber-500/10 transition-all duration-300">
                      {slide.image ? (
                        <img
                          src={slide.image}
                          alt={slide.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="text-gray-300" size={32} />
                        </div>
                      )}
                      {/* Active tag on image for mobile view */}
                      {!slide.isActive && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                          <EyeOff size={20} className="text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl font-bold text-[#102040] truncate group-hover:text-[#e07828] transition-colors">
                            {slide.title}
                          </h3>
                        </div>
                        {slide.subtitle && (
                          <p className="text-sm text-gray-500 truncate max-w-md">
                            {slide.subtitle}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                            slide.isActive
                              ? "bg-[#E8F5EE] text-[#1B8754]"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {slide.isActive ? (
                            <>
                              <CheckCircle2 size={12} className="mr-1" /> Active
                            </>
                          ) : (
                            <>
                              <XCircle size={12} className="mr-1" /> Inactive
                            </>
                          )}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold bg-[#E6EDFE] text-[#2B3674] uppercase tracking-wider">
                          # {slide.order + 1}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-y-2 gap-x-6">
                      <div className="flex items-center gap-4 text-xs font-semibold text-[#A3AED0]">
                        <span className="flex items-center gap-1.5 transition-colors hover:text-[#102040]">
                          <div className="p-1 bg-gray-50 rounded">
                            <Eye size={14} className="text-blue-500" />
                          </div>
                          {slide.viewCount?.toLocaleString() || 0}
                          <span className="font-medium text-gray-400">
                            views
                          </span>
                        </span>
                        <span className="flex items-center gap-1.5 transition-colors hover:text-[#102040]">
                          <div className="p-1 bg-gray-50 rounded">
                            <BarChart3 size={14} className="text-orange-500" />
                          </div>
                          {slide.clickCount?.toLocaleString() || 0}
                          <span className="font-medium text-gray-400">
                            clicks
                          </span>
                        </span>
                      </div>

                      {slide.cta?.text && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="font-bold text-[#102040]">
                            Button:
                          </span>
                          <span className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-gray-600 font-medium">
                            {slide.cta.text}
                          </span>
                        </div>
                      )}

                      {slide.startDate && (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 font-bold">
                          <Calendar size={13} />
                          <span>Scheduled</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Column */}
                  <div className="flex items-center gap-1 sm:ml-auto">
                    <button
                      onClick={() => handleToggleStatus(slide._id)}
                      className={`p-2.5 rounded-xl transition-all duration-200 ${
                        slide.isActive
                          ? "text-green-600 bg-green-50 hover:bg-green-100"
                          : "text-gray-400 bg-gray-50 hover:bg-gray-200"
                      }`}
                      title={
                        slide.isActive ? "Deactivate Slide" : "Activate Slide"
                      }
                    >
                      {slide.isActive ? (
                        <Eye size={20} />
                      ) : (
                        <EyeOff size={20} />
                      )}
                    </button>
                    <button
                      onClick={() => openEditModal(slide)}
                      className="p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200"
                      title="Edit Slide"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(slide._id)}
                      className={`p-2.5 rounded-xl transition-all duration-200 ${
                        deleteConfirm === slide._id
                          ? "text-red-600 bg-red-100 animate-pulse ring-2 ring-red-500/20"
                          : "text-gray-400 bg-gray-50 hover:bg-red-50 hover:text-red-500"
                      }`}
                      title={
                        deleteConfirm === slide._id
                          ? "Confirm Delete"
                          : "Delete Slide"
                      }
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        {/* Add/Edit Modal */}
        <Modal
          isOpen={modalMode === "add" || modalMode === "edit"}
          onClose={closeModal}
          title={modalMode === "add" ? "Add New Slide" : "Edit Slide"}
          className="max-w-3xl"
        >
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter slide title"
                maxLength={100}
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtitle
              </label>
              <Input
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, subtitle: e.target.value })
                }
                placeholder="Enter subtitle"
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                rows={3}
                maxLength={500}
                placeholder="Enter description"
              />
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image *
              </label>
              <div className="space-y-3">
                {/* Preview */}
                {formData.image && (
                  <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={formData.image}
                      onChange={(e) =>
                        setFormData({ ...formData, image: e.target.value })
                      }
                      placeholder="https://..."
                      icon={<ImageIcon size={16} />}
                      disabled={isUploading}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "image")}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`h-full px-4 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-200 transition-colors ${
                        isUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Upload size={18} />
                      )}
                      <span className="text-sm font-medium">Upload</span>
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Recommended size: 1920x600px or larger. Max size 5MB.
                </p>
              </div>
            </div>

            {/* Mobile Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Image (Optional)
              </label>
              <div className="space-y-3">
                {/* Preview */}
                {formData.mobileImage && (
                  <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={formData.mobileImage}
                      alt="Mobile Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={formData.mobileImage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          mobileImage: e.target.value,
                        })
                      }
                      placeholder="https://..."
                      icon={<ImageIcon size={16} />}
                      disabled={isUploading}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="mobile-image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "mobileImage")}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="mobile-image-upload"
                      className={`h-full px-4 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-200 transition-colors ${
                        isUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Upload size={18} />
                      )}
                      <span className="text-sm font-medium">Upload</span>
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Recommended size: 800x1000px (Portrait). Defaults to main
                  image if not set.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">
                Call to Action Button
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                  </label>
                  <Input
                    value={formData.cta?.text}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cta: { ...formData.cta, text: e.target.value },
                      })
                    }
                    placeholder="Learn More"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Link
                  </label>
                  <Input
                    value={formData.cta?.link}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cta: { ...formData.cta, link: e.target.value },
                      })
                    }
                    placeholder="/courses"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <Input
                    type="color"
                    value={formData.cta?.backgroundColor}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cta: {
                          ...formData.cta,
                          backgroundColor: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Color
                  </label>
                  <Input
                    type="color"
                    value={formData.cta?.textColor}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cta: { ...formData.cta, textColor: e.target.value },
                      })
                    }
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.cta?.openInNewTab}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cta: {
                          ...formData.cta,
                          openInNewTab: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">
                    Open link in new tab
                  </span>
                </label>
              </div>
            </div>

            {/* Overlay Settings */}
            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">Overview Settings</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.overlay?.enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        overlay: {
                          ...formData.overlay,
                          enabled: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Enable overlay</span>
                </label>
                {formData.overlay?.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overlay Color
                      </label>
                      <Input
                        value={formData.overlay?.color}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            overlay: {
                              ...formData.overlay,
                              color: e.target.value,
                            },
                          })
                        }
                        placeholder="rgba(0, 0, 0, 0.4)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opacity: {formData.overlay?.opacity || 0}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.overlay?.opacity || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            overlay: {
                              ...formData.overlay,
                              opacity: parseFloat(e.target.value),
                            },
                          })
                        }
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Text Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Text Position
              </label>
              <div className="flex gap-2">
                {["left", "center", "right"].map((position) => (
                  <button
                    key={position}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        textPosition: position as "left" | "center" | "right",
                      })
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      formData.textPosition === position
                        ? "bg-[#102040] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {position.charAt(0).toUpperCase() + position.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Scheduling */}
            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">
                Scheduling (Optional)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.startDate || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        startDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.endDate || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Active Status */}
            <div className="border-t pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Active (slide is visible)
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              <Save size={16} className="mr-2" />
              {isSaving ? "Saving..." : "Save Slide"}
            </Button>
          </div>
        </Modal>

        {/* Settings Modal */}
        {/* Settings Modal */}
        <Modal
          isOpen={modalMode === "settings"}
          onClose={() => setModalMode(null)}
          title="Slider Settings"
          className="max-w-2xl"
        >
          <div className="space-y-4">
            {/* Enable Slider */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sliderSettings.isActive}
                  onChange={(e) =>
                    setSliderSettings({
                      ...sliderSettings,
                      isActive: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Slider
                </span>
              </label>
            </div>

            {/* Autoplay */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sliderSettings.autoPlay}
                  onChange={(e) =>
                    setSliderSettings({
                      ...sliderSettings,
                      autoPlay: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Autoplay
                </span>
              </label>
            </div>

            {/* Autoplay Interval */}
            {sliderSettings.autoPlay && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Autoplay Interval (ms): {sliderSettings.autoPlayInterval}
                </label>
                <input
                  type="range"
                  min="2000"
                  max="10000"
                  step="1000"
                  value={sliderSettings.autoPlayInterval}
                  onChange={(e) =>
                    setSliderSettings({
                      ...sliderSettings,
                      autoPlayInterval: parseInt(e.target.value),
                    })
                  }
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>2s</span>
                  <span>10s</span>
                </div>
              </div>
            )}

            {/* Show Navigation */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sliderSettings.showNavigation}
                  onChange={(e) =>
                    setSliderSettings({
                      ...sliderSettings,
                      showNavigation: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Show Navigation Arrows
                </span>
              </label>
            </div>

            {/* Show Pagination */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sliderSettings.showPagination}
                  onChange={(e) =>
                    setSliderSettings({
                      ...sliderSettings,
                      showPagination: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Show Pagination Dots
                </span>
              </label>
            </div>

            {/* Transition Effect */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transition Effect
              </label>
              <div className="flex gap-2">
                {["slide", "fade", "zoom"].map((effect) => (
                  <button
                    key={effect}
                    onClick={() =>
                      setSliderSettings({
                        ...sliderSettings,
                        transitionEffect: effect as "slide" | "fade" | "zoom",
                      })
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      sliderSettings.transitionEffect === effect
                        ? "bg-[#102040] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {effect.charAt(0).toUpperCase() + effect.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Max Slides */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Slides: {sliderSettings.maxSlides}
              </label>
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={sliderSettings.maxSlides}
                onChange={(e) =>
                  setSliderSettings({
                    ...sliderSettings,
                    maxSlides: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>10</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
            <Button variant="outline" onClick={() => setModalMode(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSettings}
              disabled={isSaving}
            >
              <Save size={16} className="mr-2" />
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
